import { EmailConnectionRequiredBanner } from "components/EmailConnectionRequiredBanner";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { Select } from "components/ui/Select/Select";
import type {
  PromptType,
  Recipient,
  RecipientType,
  ReplyDraft,
} from "hooks/use-email-compose";
import { useComposeSenders } from "hooks/use-email-integrations-api";
import { useScrollToBottomWhile } from "hooks/use-scroll-to-bottom";
import { type FC, useState } from "react";
import { AiComposeSection } from "routes/emails/EmailDetailPage/components/AiComposeSection";
import { ReplyConfirmModal } from "routes/emails/EmailDetailPage/components/ReplyConfirmModal";

const COMMON_PROMPT_TYPES: PromptType[] = [
  "inquiry",
  "negotiate",
  "followup",
  "custom",
];

export type SourceEmailType = "hr" | "matter" | null;

export type ReplyComposePaneProps = {
  draft: ReplyDraft;
  sourceEmailType: SourceEmailType;
  isGenerating: boolean;
  isSending: boolean;
  error: string | null;
  onFieldChange: <K extends keyof ReplyDraft>(
    field: K,
    value: ReplyDraft[K],
  ) => void;
  onAddRecipient: (recipient: Recipient) => void;
  onRemoveRecipient: (email: string, type: RecipientType) => void;
  onGenerateBody: (promptType: PromptType, freeText?: string) => void;
  onSend: () => Promise<boolean>;
  onClose: () => void;
  onCancelGeneration: () => void;
};

/**
 * 返信メール作成ペイン
 * @param {ReplyComposePaneProps} props
 * @return {JSX.Element}
 */
export const ReplyComposePane: FC<ReplyComposePaneProps> = ({
  draft,
  sourceEmailType,
  isGenerating,
  isSending,
  error,
  onFieldChange,
  onAddRecipient,
  onRemoveRecipient,
  onGenerateBody,
  onSend,
  onClose,
  onCancelGeneration,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [newCcEmail, setNewCcEmail] = useState("");
  const contextReplyType: PromptType =
    sourceEmailType === "hr" ? "reply_hr" : "reply_matter";
  const availablePromptTypes: PromptType[] =
    sourceEmailType === null
      ? ["reply_matter", "reply_hr", ...COMMON_PROMPT_TYPES]
      : [contextReplyType, ...COMMON_PROMPT_TYPES];

  const { senders: senderEmails } = useComposeSenders();

  // 生成中は本文を末尾へ自動スクロールし、ストリーミング表示を常に見えるようにする
  const bodyRef = useScrollToBottomWhile(isGenerating, draft.body);

  const hasSender = senderEmails.length > 0 || !!draft.fromEmail;
  const toRecipients = draft.recipients.filter(r => r.type === "to");
  const ccRecipients = draft.recipients.filter(r => r.type === "cc");
  const bccRecipients = draft.recipients.filter(r => r.type === "bcc");

  /**
   * CC/BCCアドレスを追加する
   * @param {RecipientType} type - 宛先種別
   * @return {void}
   */
  const handleAddCcBcc = (type: RecipientType) => {
    const trimmed = newCcEmail.trim();
    if (!trimmed) return;
    onAddRecipient({ email: trimmed, name: "", type });
    setNewCcEmail("");
  };

  /**
   * 送信確認を開く
   * @return {void}
   */
  const handleOpenConfirm = () => {
    setShowConfirm(true);
  };

  /**
   * 送信を実行
   * @return {Promise<void>}
   */
  const handleConfirmSend = async () => {
    setShowConfirm(false);
    await onSend();
  };

  return (
    <div className="card bg-base-100 shadow-md">
      <div className="card-body space-y-3">
        {/* ヘッダー */}
        <div className="flex items-center justify-between">
          <h3 className="card-title text-base">返信メール作成</h3>
          <Button variant="ghost" size="xs" onClick={onClose} type="button">
            ✕
          </Button>
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="alert alert-error text-sm py-2">
            <span>{error}</span>
          </div>
        )}

        {/* 送信元 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">送信元</span>
          </label>
          {senderEmails.length === 0 && !draft.fromEmail ? (
            <EmailConnectionRequiredBanner />
          ) : (
            <Select
              options={
                senderEmails.length === 0 && draft.fromEmail
                  ? [
                      {
                        value: draft.fromEmail,
                        label: `${draft.fromName} (${draft.fromEmail})`,
                      },
                    ]
                  : senderEmails.map(s => ({
                      value: s.email,
                      label: `${s.displayName} (${s.email})`,
                    }))
              }
              value={draft.fromEmail}
              onChange={next => {
                if (!next) return;
                onFieldChange("fromEmail", next);
                const sender = senderEmails.find(s => s.email === next);
                if (sender) {
                  onFieldChange("fromName", sender.displayName);
                }
              }}
              size="sm"
            />
          )}
        </div>

        {/* 宛先 (To) */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">宛先 (To)</span>
          </label>
          <div className="space-y-1">
            {toRecipients.map(r => (
              <div
                key={r.email}
                className="input input-bordered input-sm w-full !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug"
              >
                {r.name ? `${r.name} <${r.email}>` : r.email}
              </div>
            ))}
          </div>
          {/* CC/BCC トグル */}
          {!showCcBcc && (
            <div className="mt-1">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowCcBcc(true)}
                type="button"
              >
                + CC/BCC を追加
              </Button>
            </div>
          )}
        </div>

        {/* CC/BCC セクション */}
        {showCcBcc && (
          <>
            {/* CC */}
            <div>
              <label className="label py-0">
                <span className="label-text text-xs">CC</span>
              </label>
              <div className="space-y-1">
                {ccRecipients.map(r => (
                  <div key={r.email} className="flex items-center gap-1">
                    <span className="input input-bordered input-sm flex-1 min-w-0 !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug break-all">
                      {r.email}
                    </span>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onRemoveRecipient(r.email, "cc")}
                      type="button"
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <div className="flex gap-1">
                  <input
                    type="email"
                    className="input input-bordered input-sm flex-1"
                    placeholder="cc@example.com"
                    value={newCcEmail}
                    onChange={e => setNewCcEmail(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCcBcc("cc");
                      }
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    shape="square"
                    onClick={() => handleAddCcBcc("cc")}
                    type="button"
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>

            {/* BCC */}
            <div>
              <label className="label py-0">
                <span className="label-text text-xs">BCC</span>
              </label>
              <div className="space-y-1">
                {bccRecipients.map(r => (
                  <div key={r.email} className="flex items-center gap-1">
                    <span className="input input-bordered input-sm flex-1 min-w-0 !h-auto py-1 bg-gray-50 text-sm text-gray-600 leading-snug break-all">
                      {r.email}
                    </span>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onRemoveRecipient(r.email, "bcc")}
                      type="button"
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <div className="flex gap-1">
                  <input
                    type="email"
                    className="input input-bordered input-sm flex-1"
                    placeholder="bcc@example.com"
                    value={newCcEmail}
                    onChange={e => setNewCcEmail(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCcBcc("bcc");
                      }
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    shape="square"
                    onClick={() => handleAddCcBcc("bcc")}
                    type="button"
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 件名 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">件名</span>
          </label>
          <input
            type="text"
            className="input input-bordered input-sm w-full"
            value={draft.subject}
            onChange={e => onFieldChange("subject", e.target.value)}
          />
        </div>

        {/* AI生成セクション (共通部品) */}
        <AiComposeSection
          promptTypes={availablePromptTypes}
          defaultPromptType={contextReplyType}
          isGenerating={isGenerating}
          margin={draft.margin}
          onMarginChange={value => onFieldChange("margin", value)}
          proposalInfo={draft.proposalInfo}
          onProposalInfoChange={value => onFieldChange("proposalInfo", value)}
          onGenerate={onGenerateBody}
          onCancel={onCancelGeneration}
        />

        {/* 本文 */}
        <div className="flex-1">
          <label className="label py-0">
            <span className="label-text text-xs">
              本文
              {isGenerating && (
                <span className="ml-2 text-blue-500">
                  <span className="loading loading-dots loading-xs" />{" "}
                  AI生成中...
                </span>
              )}
            </span>
          </label>
          <textarea
            ref={bodyRef}
            className="textarea textarea-bordered w-full text-sm"
            rows={12}
            value={draft.body}
            onChange={e => onFieldChange("body", e.target.value)}
            placeholder={
              isGenerating
                ? "AIがメール本文を生成しています..."
                : "本文を入力..."
            }
          />
        </div>

        {/* ボタン群 */}
        <div className="flex justify-between items-center">
          <Button variant="ghost" size="sm" onClick={onClose} type="button">
            キャンセル
          </Button>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleOpenConfirm}
              disabled={
                isGenerating || isSending || !draft.body.trim() || !hasSender
              }
              type="button"
            >
              {isSending ? (
                <>
                  <Loading size="xs" />
                  送信中...
                </>
              ) : (
                "送信"
              )}
            </Button>
          </div>
        </div>

        {/* 確認モーダル */}
        <ReplyConfirmModal
          isOpen={showConfirm}
          subject={draft.subject}
          body={draft.body}
          recipients={draft.recipients}
          fromName={draft.fromName}
          onConfirm={handleConfirmSend}
          onCancel={() => setShowConfirm(false)}
        />
      </div>
    </div>
  );
};
